import { lazy } from 'react';
import { RouteConfig, RouterConfig } from 'types';
import { childRoutes as managementChildRoutes } from 'views/Management/router';
import { childRoutes as dashboardChildRoutes } from 'views/Dashboard';
import { childRoutes as aideChildRoutes } from 'views/Aide';
import { isChannelLoginChannel } from 'views/Startups/ChannelLogin/config';
import { getPermission } from 'hooks/business/useNewPermisson';
import chatttSDK, { isMlj, hidePayment, isMabangChannel } from '../chattt';
import { NavigateWithQuery } from 'hooks/useNavigateWithQuery';
import { chatttSDKChannel } from 'services/chattt/sdk';
import { quickChatRouterControl } from 'components/RouterRender';
import BootGuideRouter from 'views/BootGuide/router';
/**
 * 404页面
 */
import NotFound from 'views/NotFound';
/**
 * 403页面
 */
import Forbidden from 'views/Forbidden';
/**
 * 主页面
 */
import Main from 'views/Main';

/**
 * 会话
 */
import Conversation from 'views/Conversation';

import WorkBench from 'views/WorkBench';
import Refund from 'views/Refund';
import RefundReturn from 'views/Refund/pages/ReturnList';
import RefundCancel from 'views/Refund/pages/CancelList';

/**
 * 评价管理
 */
import Review from 'views/Review';

import Todo from 'views/Todo';

import Aide from 'views/Aide';
import AideForbidden from 'views/Aide/Forbidden';
import { Outlet } from 'react-router-dom';
import { isAeProduct, isMasterOnline, isSiteGiant } from 'productLine';
import { hasBootGuidePermission } from 'views/BootGuide/permission';
import { getAppProductName } from '../../hooks/useTrans';

const inChattt = chatttSDK?.version;

const OutOfService = lazy(() => import('views/Management/OutOfService'));

const FindPasswordForm = lazy(() => import('views/Startups/FindPasswordForm'));

/**
 * 开店流程相关
 */
const SimpleHeaderFrame = lazy(() => import('frameworks/SimpleHeaderFrame'));
const Welcome = lazy(() => import('views/Startups/Welcome'));
const RefreshLogin = inChattt ? lazy(() => import('views/Startups/RefreshLogin')) : Welcome;
const Registry = lazy(() => import('views/Startups'));
const ShopeeAuth = lazy(() => import('views/ShopeeAuthCallback'));
const LazadaAuth = lazy(() => import('views/LazadaAuthCallback'));
const DeauthorizeResult = lazy(() => import('views/ShopeeAuthCallback/DeauthorizeResult'));
const ZhiXia = lazy(() => import('views/ZhiXia'));
const Course = lazy(() => import('views/Course'));
const Dashboard = lazy(() => import('views/Dashboard'));
const Management = lazy(() => import('views/Management'));
const VOCPlusAI = lazy(() => import('views/VOCPlusAI'));

const ReceptionScoreAssistantDetailDashboard = lazy(
  () => import('views/Dashboard/PerfReport/RececptionScore/AssistantDetail'),
);

const Galaxy = lazy(() => import('views/Galaxy'));

const Airwallex = lazy(() => import('views/Airwallex'));

/**
 * AI回复报告
 */
const AiReportReply = lazy(() => import('views/AiReport/Reply'));

const getLogin = () => {
  if (isMabangChannel) {
    return lazy(() => import('views/Startups/Login/mabang/MabangLogin'));
  }
  if (!inChattt) {
    return lazy(() => import('views/Startups/Login'));
  }
  if (isMlj) {
    return lazy(() => import('views/Startups/RefreshLogin'));
  }

  if (isChannelLoginChannel(chatttSDKChannel)) {
    return lazy(() => import('views/Startups/ChannelLogin'));
  }

  return lazy(() => import('views/Startups/Login'));
};

const Login = getLogin();

/**
 * 授权结果
 */
const ShopeeAuthResult = lazy(() => import('views/AuthShopResult/Shopee'));
const LazadaAuthResult = lazy(() => import('views/AuthShopResult/Lazada'));
const TiktokAuthResult = lazy(() => import('views/AuthShopResult/Tiktok'));
const AliExpressAuthResult = lazy(() => import('views/AuthShopResult/AliExpress'));
const AuthFromService = lazy(() => import('views/AuthShopResult/AuthFromService'));
const MercadoAuthResult = lazy(() => import('views/AuthShopResult/Mercado'));
const JolectAuthResult = lazy(() => import('views/AuthShopResult/Jolect'));

/**
 * SBS 打单报告
 */
const SBS = lazy(() => import('views/SBS'));
const MercadoFAQ = lazy(() => import('views/Mercado/FAQ'));

/**
 * 知识库
 */
const Knowledge = lazy(() => import('views/Knowledge'));
const KnowledgeDetail = lazy(() => import('views/Knowledge/Detail'));

/**
 * 其他
 */
const Pingpong = lazy(() => import('views/Pingpong'));

const NotRole = lazy(() => import('views/NotRole'));

export const mainChildRoutes: RouteConfig[] = [
  {
    path: '403',
    element: <Forbidden />,
    helmet: {
      title: '403',
    },
  },
  {
    path: 'dashboard/receptionScore/assistant',
    i18nHelmetTitle: 'ViewDashboard.nav.绩效报告',
    element: <ReceptionScoreAssistantDetailDashboard />,
    access: ({ newPermissionEnable, newPermissionMap }) =>
      getPermission({
        newPermissionEnable,
        newPermissionMap,
        permissionName: '绩效报告-查看个人绩效',
      })
        ? 'write'
        : false,
  },
  {
    path: 'dashboard',
    element: <Dashboard />,
    redirect: true,
    i18nHelmetTitle: 'nav.dashboard',
    children: dashboardChildRoutes,
    access: (props) => {
      const {
        newPermissionEnable,
        permissionMap: { SUPERVISE },
        orgValue,
      } = props;

      if (newPermissionEnable === undefined || orgValue?.isExpired) {
        return false;
      }

      if (!newPermissionEnable) {
        return SUPERVISE === true ? 'write' : false;
      }

      return dashboardChildRoutes.some(({ access }) => !access || access(props)) ? 'write' : false;
    },
  },
  {
    path: 'aideForbidden',
    element: <Outlet />,
    children: [
      {
        path: ':key',
        element: <AideForbidden />,
        i18nHelmetTitle: 'aide.跟单助手',
      },
    ],
  },
  {
    path: 'aide',
    element: <Aide />,
    i18nHelmetTitle: 'aide.跟单助手',
    children: aideChildRoutes,
    access: (props) => {
      const {
        newPermissionEnable,
        permissionMap: { SUPERVISE },
        orgValue,
      } = props;

      if (newPermissionEnable === undefined || orgValue?.isExpired) {
        return false;
      }

      if (!newPermissionEnable) {
        return SUPERVISE === true ? 'write' : false;
      }

      return aideChildRoutes.some(({ access }) => !access || access(props)) ? 'write' : false;
    },
  },
  {
    path: 'knowledge/:type',
    element: <KnowledgeDetail />,
    i18nHelmetTitle: 'AI运营-菜单名称',
    access: ({
      orgConfig: {
        whiteListConfig: {
          ENABLE_SUPPLEMENT_KNOWLEDGE,
          ENABLE_CHAT_HISTORY,
          KNOWLEDGE_SINGLE_ITEM,
          SHIPPING_AND_LOGISTICS,
          BOOTGUIDE,
        } = {},
      },
    }) =>
      ENABLE_SUPPLEMENT_KNOWLEDGE ||
      ENABLE_CHAT_HISTORY ||
      KNOWLEDGE_SINGLE_ITEM ||
      SHIPPING_AND_LOGISTICS ||
      BOOTGUIDE
        ? 'write'
        : false,
  },
  {
    path: 'knowledge',
    element: <Knowledge />,
    i18nHelmetTitle: 'AI运营-菜单名称',
    access: ({
      orgConfig: {
        whiteListConfig: {
          ENABLE_SUPPLEMENT_KNOWLEDGE,
          ENABLE_CHAT_HISTORY,
          KNOWLEDGE_SINGLE_ITEM,
          SHIPPING_AND_LOGISTICS,
          BOOTGUIDE,
        } = {},
      },
    }) =>
      ENABLE_SUPPLEMENT_KNOWLEDGE ||
      ENABLE_CHAT_HISTORY ||
      KNOWLEDGE_SINGLE_ITEM ||
      SHIPPING_AND_LOGISTICS ||
      BOOTGUIDE
        ? 'write'
        : false,
  },
  {
    path: 'course',
    i18nHelmetTitle: 'nav.专家分享课',
    element: <Course />,
    access: quickChatRouterControl(),
  },
  {
    path: 'workbench',
    element: <WorkBench />,
    redirect: true,
    children: [
      {
        path: 'conversation',
        element: <Conversation />,
        i18nHelmetTitle: 'nav.conversation',
        access: ({
          orgValue,
          orgConfig: { whiteListConfig: { AE_CONVERSATION = false } = {} },
        }) => {
          const isSbs = window.location.hash.startsWith('#/sbs');

          if (!isSbs && orgValue?.isExpired) {
            return false;
          }

          if (isAeProduct && !AE_CONVERSATION) {
            return false;
          }

          return 'write';
        },
      },
      {
        path: 'mercado-faq',
        element: <MercadoFAQ />,
        access: () => 'write',
      },
      {
        path: 'review',
        element: <Review />,
        access: ({ newPermissionEnable, newPermissionMap }) =>
          getPermission({
            newPermissionEnable,
            newPermissionMap,
            permissionName: '访问评价聚合页',
          })
            ? 'write'
            : false,
      },
      {
        path: 'refund',
        element: <Refund />,
        redirect: true,
        children: [
          {
            path: 'return',
            element: <RefundReturn />,
          },
          {
            path: 'cancel',
            element: <RefundCancel />,
          },
        ],
        access: ({ newPermissionEnable, newPermissionMap }) =>
          getPermission({
            newPermissionEnable,
            newPermissionMap,
            permissionName: '弃单处理页面',
          })
            ? 'write'
            : false,
      },
      {
        path: 'todo',
        element: <Todo />,
        access: quickChatRouterControl(({ newPermissionEnable, newPermissionMap }) =>
          getPermission({
            newPermissionEnable,
            newPermissionMap,
            permissionName: '访问待办管理中心',
          })
            ? 'write'
            : false,
        ),
      },
    ],
  },
  {
    path: 'conversation',
    element: <NavigateWithQuery to="/workbench/conversation" replace />,
  },
  {
    path: 'review',
    element: <NavigateWithQuery to="/workbench/review" replace />,
  },
  {
    path: 'zhixia',
    element: <ZhiXia />,
    i18nHelmetTitle: 'nav.zhixia',
    access: quickChatRouterControl(),
  },
  {
    path: 'vocplusai',
    element: <VOCPlusAI />,
    i18nHelmetTitle: 'nav.vocplusai',
    access: quickChatRouterControl(),
  },
  {
    path: 'management',
    redirect: true,
    i18nHelmetTitle: 'nav.配置中心',
    element: <Management />,
    children: managementChildRoutes,
    access: (props) => {
      const { newPermissionEnable } = props;

      if (newPermissionEnable === undefined) {
        return false;
      }

      if (!newPermissionEnable) {
        return 'write';
      }

      return managementChildRoutes.some(({ access }) => !access || access(props)) ? 'write' : false;
    },
  },
  {
    path: 'service',
    // 服务过期后登录，后端返回该路径，应跳转到订购页
    // 不展示订购页面时跳转到帐号管理
    element: (
      <NavigateWithQuery
        replace
        to={
          hidePayment
            ? isMasterOnline
              ? '/outofservice'
              : '/management/staff/account'
            : '/outofservice'
        }
      />
    ),
  },
  {
    path: 'refund',
    element: <NavigateWithQuery to="/workbench/refund" replace />,
    children: [
      {
        path: 'return',
        element: <NavigateWithQuery to="/workbench/refund/return" replace />,
      },
      {
        path: 'cancel',
        element: <NavigateWithQuery to="/workbench/refund/cancel" replace />,
      },
    ],
  },
  {
    path: 'aiReport/reply/:id',
    element: <AiReportReply />,
    i18nHelmetTitle: 'AI精准回复试用报告',
    access: (props) => {
      return hasBootGuidePermission(props) ? 'write' : false;
    },
  },
  {
    path: 'outofservice',
    element: <OutOfService />,
    helmet: {
      title: 'out of service',
    },
  },
  {
    path: 'notRole',
    element: <NotRole />,
    helmet: {
      title: 'Not role',
    },
  },
  {
    path: '*',
    element: <NotFound />,
    helmet: {
      title: '404',
    },
  },
  BootGuideRouter,
];

/**
 * 路由配置
 */
const routerConfig: RouterConfig = {
  history: 'hash',
  routes: [
    {
      path: ':info/shopeeauthcallback',
      element: <ShopeeAuth />,
      helmet: {
        title: 'Pending',
      },
    },
    {
      path: ':info/lazadaauthcallback',
      element: <LazadaAuth />,
      helmet: {
        title: 'Pending',
      },
    },
    {
      path: 'deauthorize/result',
      element: <DeauthorizeResult />,
    },
    {
      path: 'auth/shop/result/shopee/:type/:productLine',
      element: <ShopeeAuthResult />,
    },
    {
      path: 'auth/shop/result/lazada/:type/:productLine',
      element: <LazadaAuthResult />,
    },
    {
      path: 'auth/shop/result/lazada/enter/:code',
      element: <AuthFromService title="Lazada" />,
    },
    {
      path: 'auth/shop/result/tiktok/:type/:productLine',
      element: <TiktokAuthResult />,
    },
    {
      path: 'auth/shop/result/tiktok_us/:type/:productLine',
      element: <TiktokAuthResult />,
    },
    {
      path: 'auth/shop/result/aliexpress/:type',
      element: <AliExpressAuthResult />,
    },
    {
      path: 'auth/shop/result/tiktok/enter/:code',
      element: <AuthFromService title="TikTok" />,
    },
    {
      path: 'auth/shop/result/tiktok_us/enter/:code',
      element: <AuthFromService title="TikTok_US" />,
    },
    {
      path: 'auth/shop/result/:platform/:type/:productLine',
      element: <MercadoAuthResult />,
    },
    {
      path: 'auth/shop/result/jolect/:code',
      element: <JolectAuthResult />,
    },
    {
      path: 'welcome',
      element: <Welcome />,
      helmet: {
        title: getAppProductName(),
      },
    },
    {
      path: '/',
      redirect: true,
      element: <SimpleHeaderFrame headerType={isMlj ? 'channel' : 'default'} />,
      children: [
        {
          path: 'login',
          element: <Login />,
          helmet: {
            title: getAppProductName(),
          },
        },
        {
          path: '/refresh/login',
          element: <RefreshLogin />,
          helmet: {
            title: getAppProductName(),
          },
        },
        ...(isSiteGiant
          ? []
          : [
              {
                path: 'registry',
                element: <Registry />,
                helmet: {
                  title: getAppProductName(),
                },
              },
            ]),
        {
          path: 'findPassword',
          element: <FindPasswordForm />,
          helmet: {
            title: getAppProductName(),
          },
        },
      ],
    },
    {
      path: '/galaxy',
      element: <Galaxy iframeMode />,
    },
    {
      path: '/sbs',
      element: <SBS />,
      access: quickChatRouterControl(({ permissionMap: { ASSIGN, SUPERVISE } }) =>
        ASSIGN || SUPERVISE ? 'write' : false,
      ),
    },
    {
      path: '/airwallex',
      element: <Airwallex />,
      helmet: {
        defaultTitle: 'Pay',
      },
    },
    {
      path: '/pingpong',
      element: <Pingpong />,
      helmet: {
        defaultTitle: 'Pay',
      },
    },
    {
      path: '/',
      redirect: true,
      element: <Main />,
      isMain: true,
      children: mainChildRoutes,
    },
  ],
};

export default routerConfig;
